@reference "./index.css";

/* ═══════════════════════════════════════════════════════════
   ERROR BOUNDARY & LAYOUT
═══════════════════════════════════════════════════════════ */
.errorContainer {
  @apply min-h-screen flex items-center justify-center bg-leap-cream p-6;
}

.errorCard {
  background: rgba(254, 250, 232, 0.86);
  border: 1px solid rgba(224, 183, 136, 0.26);
  border-radius: 0.75rem;
  box-shadow: 0 8px 40px rgba(51, 75, 70, 0.09);
  padding: 2rem;
  max-width: 28rem;
  text-align: center;
}

.errorTitle {
  @apply text-2xl font-bold text-leap-dark mb-2;
  font-family: 'Playfair Display', serif;
}

.errorMessage {
  @apply text-leap-olive mb-6;
}

.errorButton {
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 0.875rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #1a1008;
  background: linear-gradient(135deg, #fae185 0%, #de9a49 55%, #c07830 100%);
  border: none;
  border-radius: 1rem;
  padding: 0.75rem 2rem;
  box-shadow: 0 6px 20px rgba(222, 154, 73, 0.4);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: filter 0.2s, transform 0.15s;
}

.errorButton:hover {
  filter: brightness(1.1);
  transform: translateY(-2px);
}

/* ═══════════════════════════════════════════════════════════
   FIREFLIES
═══════════════════════════════════════════════════════════ */
.firefliesContainer {
  @apply absolute inset-0 z-3 pointer-events-none overflow-hidden;
}

.firefly {
  position: absolute;
  border-radius: 50%;
  animation: firefly var(--firefly-duration) ease-in-out infinite,
    pulse var(--firefly-pulse) ease-in-out infinite;
}

@keyframes firefly {
  0%,
  100% {
    transform: translate(0, 0);
    opacity: 0;
  }
  25% {
    opacity: 1;
  }
  50% {
    transform: translate(var(--drift-x), var(--drift-y));
    opacity: 0.8;
  }
  75% {
    opacity: 1;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

@keyframes spinRing {
  to {
    transform: rotate(360deg);
  }
}

/* ═══════════════════════════════════════════════════════════
   ANIMATED COUNTER & HERO STATS
═══════════════════════════════════════════════════════════ */
.counterWrapper {
  text-align: center;
  padding: 0 24px;
}

.counterValue {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.6rem, 2.8vw, 2.4rem);
  font-weight: 800;
  color: #fae185;
  line-height: 1;
}

.counterLabel {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(222, 154, 73, 0.7);
  margin-top: 5px;
}

.heroStatsContainer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 18px 0;
  border-top: 1px solid rgba(222, 154, 73, 0.18);
  border-bottom: 1px solid rgba(222, 154, 73, 0.18);
  background: rgba(0, 0, 0, 0.14);
  border-radius: 10px;
  max-width: 560px;
  margin: 2.5rem auto 0;
}

.statsDivider {
  width: 1px;
  height: 36px;
  background: rgba(222, 154, 73, 0.22);
}

.tooltipBox {
  position: fixed;
  bottom: auto;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(8, 5, 2, 0.94);
  border: 1px solid rgba(222, 154, 73, 0.52);
  border-radius: 8px;
  padding: 8px 18px;
  pointer-events: none;
  z-index: 20;
  backdrop-filter: blur(14px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.55);
  white-space: nowrap;
}

.tooltipLabel {
  font-family: 'Playfair Display', serif;
  font-size: 14px;
  color: #fae185;
  margin: 0;
  font-weight: 700;
}

.tooltipDescription {
  font-family: 'DM Sans', sans-serif;
  font-size: 11px;
  color: rgba(249, 236, 182, 0.52);
  margin: 0;
  margin-top: 2px;
}

/* ═══════════════════════════════════════════════════════════
   SUBTHEMES SECTION
═══════════════════════════════════════════════════════════ */
.subthemesSection {
  composes: subthemes-section from global;
}

.subthemesInner {
  composes: subthemes-inner from global;
}

.subthemesLabel {
  composes: subthemes-label from global;
}

.subthemesRow {
  composes: subthemes-row from global;
  gap: 0.8rem;
}

.subthemesCompactShell {
  position: relative;
  margin-top: 1rem;
  background: transparent;
  padding: 2rem 0 2.5rem;
  width: 100%;
  overflow: visible;
  box-sizing: border-box;
}

.subthemesCompactInner {
  width: min(1400px, 100vw);
  margin: 0 auto;
  padding: 0 clamp(0.5rem, 2vw, 1rem);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  max-width: 100%;
  overflow: visible;
}

.subthemesBackdropTop {
  position: absolute;
  top: -1.5rem;
  left: 3rem;
  width: 16rem;
  height: 16rem;
  border-radius: 9999px;
  filter: blur(64px);
  animation: pulse 2s infinite;
  background: rgba(74, 176, 154, 0.08);
  animation-duration: 4s;
  pointer-events: none;
}

.subthemesBackdropRight {
  position: absolute;
  top: -0.5rem;
  right: 3rem;
  width: 18rem;
  height: 18rem;
  border-radius: 9999px;
  filter: blur(64px);
  animation: pulse 2s infinite;
  background: rgba(17, 26, 14, 0.15);
  animation-duration: 5.5s;
  animation-delay: 1s;
  pointer-events: none;
}

.subthemesCompactLabel {
  font-family: 'Tropikal', serif !important;
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(249, 236, 182, 0.74);
  margin-bottom: 1.15rem;
}

.subthemesRowCompact {
  gap: 0.75rem;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  flex-wrap: nowrap !important;
  justify-content: center !important;
  scroll-snap-type: x proximity;
  scroll-padding-inline: 0.25rem;
  padding-inline: 0.25rem;
  padding-bottom: 12px;
  padding-top: 26px;
  box-sizing: border-box;
}

@media (max-width: 1100px) {
  .subthemesRowCompact {
    justify-content: flex-start !important;
  }
}

.subthemesRowCompact::-webkit-scrollbar {
  display: none;
}

.subthemeAssetButton {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.8rem 1rem;
  min-width: 130px;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  gap: 0.8rem;
  transition: transform 0.2s ease, filter 0.2s ease, background 0.2s ease,
    border-color 0.2s ease, box-shadow 0.2s ease;
  backdrop-filter: none;
  box-shadow: none;
  flex-shrink: 0;
  scroll-snap-align: start;
}

.subthemeAssetButton:hover {
  transform: translateY(-4px) scale(1.08);
  background: transparent;
  border-color: transparent;
  box-shadow: none;
}

.subthemeAssetIcon {
  width: 70px;
  height: 70px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(249, 236, 182, 0.82);
  background: radial-gradient(
      circle at 30% 22%,
      rgba(250, 225, 133, 0.25) 0%,
      rgba(250, 225, 133, 0.04) 35%,
      transparent 62%
    ),
    linear-gradient(
      165deg,
      rgba(19, 26, 18, 0.9) 0%,
      rgba(9, 13, 9, 0.76) 100%
    );
  border: 1px solid rgba(222, 154, 73, 0.26);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12),
    0 5px 12px rgba(0, 0, 0, 0.26), 0 0 24px rgba(222, 154, 73, 0.08);
  transition: border-color 0.2s ease, box-shadow 0.25s ease, color 0.2s ease,
    transform 0.2s ease;
  animation: pulse 4s ease-in-out infinite;
}

.subthemeAssetLabel {
  font-family: 'Tropikal', 'Playfair Display', serif !important;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: none;
  color: rgba(249, 236, 182, 0.85);
  max-width: 150px;
  text-align: center;
  line-height: 1.4;
  white-space: normal; 
  word-wrap: break-word;
}

@media (max-width: 768px) {
  .subthemeAssetButton {
    min-width: 150px;
    padding: 1rem 1.2rem;
    gap: 1rem;
  }

  .subthemeAssetIcon {
    width: 85px;
    height: 85px;
  }

  .subthemeAssetLabel {
    font-size: 1.05rem;
    max-width: 160px;
  }
}

.subthemeAssetButton:hover .subthemeAssetIcon {
  color: #fae185;
  border-color: rgba(250, 225, 133, 0.48);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 10px 22px rgba(0, 0, 0, 0.28), 0 0 30px rgba(250, 225, 133, 0.22);
}

.subthemeAssetButton:hover .subthemeAssetLabel {
  color: rgba(250, 225, 133, 0.95);
}

.subthemeAssetActive .subthemeAssetIcon {
  color: #fae185;
  border-color: rgba(250, 225, 133, 0.72);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22),
    0 0 0 3px rgba(250, 225, 133, 0.22), 0 0 30px rgba(250, 225, 133, 0.7),
    0 10px 26px rgba(0, 0, 0, 0.34);
  transform: translateY(-1px) scale(1.1);
}

.subthemeAssetActive .subthemeAssetLabel {
  color: #fae185;
  text-shadow: 0 0 10px rgba(250, 225, 133, 0.5);
}

@media (max-width: 768px) {
  .subthemesCompactShell {
    padding: 1.2rem 0 1.7rem;
  }

  .subthemesRowCompact {
    overflow-x: clip;
    scroll-snap-type: none;
    justify-content: center !important;
    flex-wrap: wrap !important;
    gap: 0.55rem;
    padding-inline: 0;
    padding-top: 12px;
    padding-bottom: 8px;
  }

  .subthemeAssetIcon {
    width: 60px;
    height: 60px;
  }

  .subthemeAssetLabel {
    font-size: 0.56rem;
    max-width: 78px;
    white-space: normal;
    text-align: center;
    letter-spacing: 0.08em;
  }

  .subthemeAssetButton {
    min-width: 0;
    flex: 1 1 calc((100% - 1.1rem) / 3);
    max-width: 120px;
    padding: 0.5rem 0.56rem;
    gap: 0.42rem;
    border-radius: 10px;
  }

  .subthemesCompactLabel {
    font-size: 1rem;
    margin-bottom: 0.85rem;
  }

  .subthemesBackdropTop,
  .subthemesBackdropRight {
    filter: blur(56px);
    opacity: 0.7;
  }
}

@media (max-width: 480px) {
  .subthemesCompactShell {
    padding: 1rem 0 1.4rem;
  }

  .subthemesCompactLabel {
    font-size: 0.92rem;
    letter-spacing: 0.07em;
  }

  .subthemesRowCompact {
    gap: 0.45rem;
    justify-content: center !important;
    padding-top: 8px;
    padding-bottom: 6px;
  }

  .subthemeAssetIcon {
    width: 52px;
    height: 52px;
  }

  .subthemeAssetButton {
    min-width: 0;
    flex: 1 1 calc((100% - 0.45rem) / 2);
    max-width: 150px;
    padding: 0.44rem 0.48rem;
    gap: 0.35rem;
  }

  .subthemeAssetLabel {
    font-size: 0.5rem;
    max-width: 64px;
    letter-spacing: 0.07em;
  }

  .subthemesBackdropTop,
  .subthemesBackdropRight {
    display: none;
  }
}

.subthemePill {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.52rem;
  width: 124px;
  min-width: 124px;
  max-width: 124px;
  min-height: 102px;
  padding: 0.6rem 0.5rem;
  border-radius: 12px;
  background: rgba(254, 250, 232, 0.9);
  border: 1.5px solid rgba(224, 183, 136, 0.34);
  box-shadow: 0 3px 12px rgba(51, 75, 70, 0.08);
  color: #334b46;
  cursor: pointer;
  transition: transform 0.22s cubic-bezier(0.34, 1.2, 0.64, 1), background 0.2s,
    border-color 0.2s, box-shadow 0.2s;
}

.subthemePill:hover,
.subthemePillActive {
  background: #334b46;
  border-color: #334b46;
  color: #fae185;
  transform: translateY(-4px) scale(1.03);
  box-shadow: 0 10px 28px rgba(51, 75, 70, 0.24);
}

.subthemePillIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c6b4b;
  transition: color 0.2s, transform 0.2s;
}

.subthemePill:hover .subthemePillIcon,
.subthemePillActive .subthemePillIcon {
  color: #fae185;
  transform: translateY(-1px);
}

.subthemePillLabel {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.56rem;
  font-weight: 800;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  line-height: 1.24;
  text-align: center;
  color: rgba(51, 75, 70, 0.78);
  transition: color 0.2s;
}

.subthemePill:hover .subthemePillLabel,
.subthemePillActive .subthemePillLabel {
  color: rgba(249, 236, 182, 0.94);
}

@media (max-width: 768px) {
  .subthemePill {
    width: 108px;
    min-width: 108px;
    max-width: 108px;
    min-height: 96px;
    padding: 0.5rem 0.4rem;
  }

  .subthemePillLabel {
    font-size: 0.52rem;
    letter-spacing: 0.1em;
  }
}

/* ═══════════════════════════════════════════════════════════
   CLASS CARD
═══════════════════════════════════════════════════════════ */
.classCardWrapper {
  background: linear-gradient(
    180deg,
    rgba(255, 252, 241, 0.92) 0%,
    rgba(246, 234, 198, 0.9) 100%
  );
  border: 1px solid rgba(224, 183, 136, 0.26);
  border-radius: 0.75rem;
  overflow: hidden;
  transition: transform 0.35s cubic-bezier(0.34, 1.2, 0.64, 1),
    box-shadow 0.35s ease, border-color 0.2s;
  box-shadow: 0 10px 28px rgba(51, 75, 70, 0.08);
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  min-height: 280px;
  width: 100%;
  box-sizing: border-box;
}

.classCardWrapper:hover {
  transform: translateY(-7px);
  box-shadow: 0 24px 62px rgba(51, 75, 70, 0.14),
    0 10px 22px rgba(222, 154, 73, 0.12);
  border-color: rgba(224, 183, 136, 0.58);
}

.classGrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.8rem;
}

@media (min-width: 640px) {
  .classGrid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}

@media (min-width: 1024px) {
  .classGrid {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
  }
}

.cardHeaderBar {
  composes: card-header-bar from global;
}

.cardOrgLogo {
  composes: card-org-logo from global;
}

.cardOrgLogoPlaceholder {
  composes: card-org-logo-placeholder from global;
}

.cardBadge {
  composes: card-badge from global;
  font-family: 'Tropikal', 'Playfair Display', serif !important;
  font-size: 0.78rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.03em !important;
  text-transform: none !important;
  padding: 0.32rem 0.85rem !important;
  border-radius: 6px !important;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(250, 225, 133, 0.15);
}

.cardBadgeTheme {
  composes: card-badge-theme from global;
  background: rgba(20, 30, 25, 0.92) !important;
  color: #ffeaa3 !important;
  border: 1.5px solid rgba(250, 225, 133, 0.45) !important;
  backdrop-filter: blur(12px);
}

.cardBadgeDay {
  composes: card-badge-day from global;
}

.cardImageWrapper {
  position: relative;
  overflow: hidden;
  width: 100%;
  min-height: 200px;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .cardImageWrapper {
    min-height: 220px;
  }
}

.cardImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cardImageGradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.88) 0%,
    rgba(0, 0, 0, 0.65) 35%,
    rgba(0, 0, 0, 0.35) 65%,
    rgba(0, 0, 0, 0.12) 85%,
    transparent 100%
  );
  z-index: 2;
  pointer-events: none;
}

.cardSlotsLabel {
  position: absolute;
  top: 12px;
  right: 12px;
  width: auto;
  height: auto;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(12px);
  border-radius: 8px;
  border: 1.5px solid rgba(250, 225, 133, 0.35);
  padding: 6px 12px;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: #fae185;
  font-family: 'DM Sans', sans-serif;
  z-index: 10;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

.cardDetailsSection {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  background: linear-gradient(135deg, rgba(255,252,241,0.96), rgba(253,247,228,0.94));
  border-top: 1px solid rgba(210,175,110,0.15);
}

.cardOverlayContent {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1rem;
  display: none;
  flex-direction: column;
  gap: 0.5rem;
}

.cardOverlayTopRow {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.1rem;
}

.cardContent {
  @apply p-5 flex flex-col;
  width: 100%;
  min-width: 0;
}

.cardOrganization {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #803e2f;
  margin-bottom: 0.1rem;
  text-shadow: none;
}

.cardTitle {
  font-family: 'Tropikal', serif;
  font-size: clamp(1.05rem, 1.25vw, 1.35rem);
  line-height: 1.05;
  letter-spacing: 0.01em;
  color: #803e2f;
  cursor: pointer;
  margin-bottom: 0.3rem;
  font-weight: 700;
  text-shadow: none;
}

.cardMetadata {
  @apply space-y-1.5 mb-4 text-sm text-leap-dark/60;
  font-size: 0.78rem;
}

.cardMetadataOverlay {
  @apply space-y-1;
  font-size: 0.72rem;
  color: #803e2f;
  margin-bottom: 0.35rem;
  text-shadow: none;
}

.metadataItem {
  @apply flex items-center gap-2;
}

.metadataIcon {
  color: #803e2f;
  flex-shrink: 0;
}

.cardActions {
  @apply mt-auto flex flex-col gap-1.5;
}

.registerBtn {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #f9ecb6;
  background: #334b46;
  border: none;
  border-radius: 0.375rem;
  padding: 0.82rem 1rem;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-decoration: none;
  transition: background 0.2s, transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 3px 10px rgba(51, 75, 70, 0.2);
}

.registerBtn:hover {
  background: #803e2f;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(128, 62, 47, 0.32);
}

.learnMoreBtn {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #b05a32;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  transition: gap 0.2s, color 0.2s;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: none;
  padding: 0.6rem;
  width: 100%;
}

.learnMoreBtn:hover {
  color: #803e2f;
  gap: 0.55rem;
}

.cardActionsOverlay {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.registerBtnOverlay {
  background: linear-gradient(135deg, #fae185 0%, #de9a49 60%, #c07830 100%);
  color: #1a1008;
  border: none;
  border-radius: 0.4rem;
  padding: 0.5rem 0.7rem;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  text-decoration: none;
  transition: filter 0.2s ease;
}

.registerBtnOverlay:hover {
  filter: brightness(1.08);
}

.learnMoreBtnOverlay {
  background: rgba(15, 10, 4, 0.55);
  border: 1px solid rgba(250, 225, 133, 0.45);
  color: #fae185;
  border-radius: 0.4rem;
  padding: 0.5rem 0.7rem;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.learnMoreBtnOverlay:hover {
  background: rgba(15, 10, 4, 0.78);
}

@media (max-width: 700px) {
  .cardImageWrapper {
    min-height: 230px;
  }

  .cardOverlayContent {
    padding: 0.75rem;
  }

  .cardActionsOverlay {
    flex-wrap: wrap;
  }

  .classCardWrapper {
    min-height: 230px;
  }
}

/* ═══════════════════════════════════════════════════════════
   ADMIN DASHBOARD
═══════════════════════════════════════════════════════════ */
.adminWrapper {
  @apply container mx-auto px-4 py-12;
}

.adminHeader {
  @apply flex items-center gap-4 mb-8;
}

.adminBackBtn {
  @apply p-2 hover:bg-leap-tan/30 rounded-full transition-colors;
}

.adminTitle {
  @apply text-3xl font-bold text-leap-dark;
  font-family: 'Playfair Display', serif;
}

.adminCard {
  background: rgba(254, 250, 232, 0.82);
  backdrop-filter: blur(18px) saturate(1.4);
  -webkit-backdrop-filter: blur(18px) saturate(1.4);
  border: 1px solid rgba(224, 183, 136, 0.3);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.82),
    0 4px 28px rgba(51, 75, 70, 0.08), 0 1px 4px rgba(222, 154, 73, 0.07);
  padding: 3rem;
  border-radius: 1.5rem;
  text-align: center;
  max-width: 42rem;
  margin: 0 auto;
}

.adminIconWrap {
  width: 80px;
  height: 80px;
  border-radius: 0.5625rem;
  background: rgba(222, 154, 73, 0.1);
  border: 1px solid rgba(222, 154, 73, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b05a32;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.2s;
  margin: 0 auto 1.5rem;
}

.adminIconWrap:hover {
  background: rgba(222, 154, 73, 0.18);
  transform: scale(1.05);
}

.adminCardTitle {
  @apply text-2xl font-bold text-leap-dark mb-4;
  font-family: 'Playfair Display', serif;
}

.adminCardMessage {
  @apply text-leap-olive mb-8 text-lg;
}

.adminCTABtn {
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 0.875rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #1a1008;
  background: linear-gradient(135deg, #fae185 0%, #de9a49 55%, #c07830 100%);
  border: none;
  border-radius: 1rem;
  padding: 1rem 2rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28),
    0 6px 28px rgba(222, 154, 73, 0.5), 0 2px 8px rgba(0, 0, 0, 0.35);
  transition: filter 0.2s, transform 0.15s, box-shadow 0.2s;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  position: relative;
  overflow: hidden;
}

.adminCTABtn:hover {
  filter: brightness(1.1);
  transform: translateY(-2px);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28),
    0 10px 36px rgba(222, 154, 73, 0.6), 0 3px 10px rgba(0, 0, 0, 0.4);
}

/* ═══════════════════════════════════════════════════════════
   MAIN NAVIGATION
═══════════════════════════════════════════════════════════ */
.appContainer {
  @apply min-h-screen flex flex-col;
  overflow: clip;
  overflow-x: clip;
  max-width: 100vw;
}

.navBar {
  @apply fixed top-0 w-full z-50 transition-all duration-300;
}

@media (max-width: 768px) {
  .navBar {
    background:
      radial-gradient(ellipse 55% 45% at 8% 85%, rgba(74, 176, 154, 0.18) 0%, transparent 55%),
      radial-gradient(ellipse 45% 35% at 94% 12%, rgba(222, 154, 73, 0.12) 0%, transparent 50%),
      linear-gradient(158deg, #0e1a0c 0%, #152010 30%, #1c2d18 60%, #111a0e 100%);
    filter: blur(8px);
    backdrop-filter: blur(8px);
  }
}

.navBarScrolled {
  background: rgba(12, 9, 4, 0.97) !important;
  backdrop-filter: blur(28px) saturate(1.5) !important;
  border-bottom: 1px solid rgba(222, 154, 73, 0.22) !important;
  box-shadow: 0 2px 40px rgba(0, 0, 0, 0.65) !important;
  padding: 0.5rem 0 !important;
}

@media (max-width: 768px) {
  .navBarScrolled {
    background:
      radial-gradient(ellipse 55% 45% at 8% 85%, rgba(74, 176, 154, 0.18) 0%, transparent 55%),
      radial-gradient(ellipse 45% 35% at 94% 12%, rgba(222, 154, 73, 0.12) 0%, transparent 50%),
      linear-gradient(158deg, #0e1a0c 0%, #152010 30%, #1c2d18 60%, #111a0e 100%) !important;
    filter: blur(8px) !important;
    backdrop-filter: blur(8px) !important;
  }
}

.navBarHome {
  background: transparent !important;
  box-shadow: none !important;
  border-bottom: none !important;
  padding: 1rem 0;
}

@media (max-width: 768px) {
  .navBarHome {
    background:
      radial-gradient(ellipse 55% 45% at 8% 85%, rgba(74, 176, 154, 0.18) 0%, transparent 55%),
      radial-gradient(ellipse 45% 35% at 94% 12%, rgba(222, 154, 73, 0.12) 0%, transparent 50%),
      linear-gradient(158deg, #0e1a0c 0%, #152010 30%, #1c2d18 60%, #111a0e 100%) !important;
    filter: blur(8px) !important;
  }
}

.navBarLight {
  background: rgba(249, 236, 182, 0.96) !important;
  backdrop-filter: blur(20px) !important;
  border-bottom: 1px solid rgba(222, 154, 73, 0.2) !important;
  box-shadow: 0 2px 20px rgba(51, 75, 70, 0.08) !important;
  padding: 1rem 0 !important;
}

/* ── NAV INNER: 3-col desktop, 2-col mobile ── */
.navInner {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.75rem;
  width: 100%;
  box-sizing: border-box;
}

.navLogo {
  justify-self: start;
  display: flex;
  align-items: center;
  cursor: pointer;
  grid-column: 1;
}

.navLogoImg {
  height: 42px;
  width: auto;
  mix-blend-mode: screen;
}

.navCenter {
  justify-self: center;
  grid-column: 2;
  display: none;
  align-items: center;
  gap: 2rem;
}

@media (min-width: 769px) {
  .navCenter {
    display: flex;
  }
}

.navLink {
  composes: nav-link from global;
}

.navLinkActive {
  composes: nav-link from global;
  color: #fae185;
  font-weight: 600;
}

.navRight {
  justify-self: end;
  grid-column: 3;
  display: none;
  align-items: center;
  gap: 0.5rem;
}

@media (min-width: 769px) {
  .navRight {
    display: flex;
  }
}

.navIconBtn {
  composes: nav-icon-btn from global;
}

.navProfileImg {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
}

.adminLink {
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #de9a49;
  transition: color 0.2s;
  background: none;
  border: none;
  cursor: pointer;
}

.adminLink:hover {
  color: #fae185;
}

.navSignOutBtn {
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #1a1008;
  background: linear-gradient(135deg, #fae185 0%, #de9a49 55%, #c07830 100%);
  border: none;
  border-radius: 0.375rem;
  padding: 0.45rem 1rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28),
    0 6px 28px rgba(222, 154, 73, 0.5), 0 2px 8px rgba(0, 0, 0, 0.35);
  transition: filter 0.2s, transform 0.15s, box-shadow 0.2s;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
  position: relative;
  overflow: hidden;
}

.navSignOutBtn:hover {
  filter: brightness(1.1);
  transform: translateY(-2px);
}

.navRegisterBtn {
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #1a1008;
  background: linear-gradient(135deg, #fae185 0%, #de9a49 55%, #c07830 100%);
  border: none;
  border-radius: 0.375rem;
  padding: 0.45rem 1rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28),
    0 6px 28px rgba(222, 154, 73, 0.5), 0 2px 8px rgba(0, 0, 0, 0.35);
  transition: filter 0.2s, transform 0.15s, box-shadow 0.2s;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
  position: relative;
  overflow: hidden;
}

.navRegisterBtn:hover {
  filter: brightness(1.1);
  transform: translateY(-2px);
}

/* ── MOBILE TOGGLE ── */
.navMobileToggle {
  display: none;
  justify-self: end;
  align-self: center;
  grid-column: 3;
  gap: 0.5rem;
  align-items: center;
}

.navMobileBtn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f9ecb6;
  transition: color 0.2s;
}

/* ═══════════════════════════════════════════════════════════
   MOBILE MENU
═══════════════════════════════════════════════════════════ */
.mobileMenu {
  position: fixed;
  inset: 0;
  z-index: 40;
  background:
    radial-gradient(ellipse 55% 45% at 8% 85%, rgba(74, 176, 200, 0.18) 0%, transparent 55%),
    radial-gradient(ellipse 45% 35% at 94% 12%, rgba(100, 180, 220, 0.12) 0%, transparent 50%),
    linear-gradient(158deg, #0a1428 0%, #0d1f2d 30%, #1a3a52 60%, #0d1f2d 100%);
  padding-top: 6rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  display: none;
  overflow-y: auto;
  overflow-x: hidden;
}

.mobileMenuContent {
  @apply flex flex-col gap-6 text-2xl font-bold;
  font-family: 'Playfair Display', serif;
  color: #f9ecb6;
}

.mobileMenuItem {
  @apply text-left hover:text-leap-yellow transition-colors;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #f9ecb6;
  padding: 0;
}

.mobileMenuSignOutBtn {
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #1a1008;
  background: linear-gradient(135deg, #fae185 0%, #de9a49 55%, #c07830 100%);
  border: none;
  border-radius: 0.875rem;
  padding: 1rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28),
    0 6px 28px rgba(222, 154, 73, 0.5);
  transition: filter 0.2s, transform 0.15s;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  width: 100%;
}

.mobileMenuSignOutBtn:hover {
  filter: brightness(1.1);
  transform: translateY(-2px);
}

.mobileMenuSignInBtn {
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #1a1008;
  background: linear-gradient(135deg, #fae185 0%, #de9a49 55%, #c07830 100%);
  border: none;
  border-radius: 0.875rem;
  padding: 1rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28),
    0 6px 28px rgba(222, 154, 73, 0.5);
  transition: filter 0.2s, transform 0.15s;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  width: 100%;
}

.mobileMenuSignInBtn:hover {
  filter: brightness(1.1);
  transform: translateY(-2px);
}

/* ═══════════════════════════════════════════════════════════
   HERO SECTION
═══════════════════════════════════════════════════════════ */
.heroSection {
  composes: hero-bg from global;
  position: relative;
  display: grid;
  place-items: center;
  overflow: hidden;
  min-height: 100vh;
  min-height: 100svh;
  min-height: 100dvh;
  padding-top: 0;
  padding-bottom: clamp(22px, 3.4vw, 42px);
  width: 100%;
  box-sizing: border-box;
  margin-top: -1px;
  contain: layout style paint;
}

.heroBackdropTop {
  position: absolute;
  top: 4rem;
  left: 2rem;
  width: 18rem;
  height: 18rem;
  border-radius: 9999px;
  filter: blur(64px);
  animation: pulse 2s infinite;
  background: rgba(222, 154, 73, 0.08);
  animation-duration: 4s;
}

.heroBackdropRight {
  position: absolute;
  top: 6rem;
  right: 3rem;
  width: 20rem;
  height: 20rem;
  border-radius: 9999px;
  filter: blur(64px);
  animation: pulse 2s infinite;
  background: rgba(74, 176, 154, 0.06);
  animation-duration: 5.5s;
  animation-delay: 1s;
}

.heroBackdropContainer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.heroContent {
  max-width: 56rem;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: clamp(4.75rem, 10vh, 6.5rem) 1rem clamp(7rem, 14vh, 9rem);
  box-sizing: border-box;
}

.heroLogo {
  width: min(480px, 85vw);
  display: block;
  margin: 0 auto;
  mix-blend-mode: screen;
  filter: drop-shadow(0 0 48px rgba(222, 154, 73, 0.65))
    drop-shadow(0 0 96px rgba(222, 154, 73, 0.3))
    drop-shadow(0 14px 36px rgba(0, 0, 0, 0.7));
  animation: heroFloat 5.5s ease-in-out infinite;
  will-change: transform;
}

.heroEyebrow {
  font-family: 'Tropikal', serif;
  font-size: clamp(0.95rem, 1.15vw, 1.15rem);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: none;
  color: #de9a49;
  background: rgba(222, 154, 73, 0.1);
  border: 1px solid rgba(222, 154, 73, 0.32);
  padding: 0.52rem 1.45rem;
  border-radius: 999px;
  display: inline-block;
  position: relative;
  overflow: hidden;
  margin-bottom: 1.5rem;
  max-width: 90vw;
}

.fade-up {
  animation: fadeUp 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.delay-1 {
  animation-delay: 0.12s;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.heroSubtext {
  font-family: 'DM Sans', sans-serif;
  font-weight: 300;
  font-size: clamp(1rem, 2.2vw, 1.2rem);
  color: rgba(224, 210, 175, 0.72);
  max-width: 540px;
  margin: 0.75rem auto 2.5rem;
  line-height: 1.8;
}

.heroCTAButtons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  animation: fadeUp 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.38s;
}

@media (min-width: 640px) {
  .heroCTAButtons {
    flex-direction: row;
  }
}

/* ═══════════════════════════════════════════════════════════
   FOOTER
═══════════════════════════════════════════════════════════ */
.footer {
  background: linear-gradient(180deg, #1d2e28 0%, #141f1b 100%);
  border-top: 1px solid rgba(222, 154, 73, 0.14);
  color: #f9ecb6;
  padding: 4rem 1rem;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  min-height: 850px;
  contain: layout style;
}

.footerContainer {
  @apply container mx-auto grid grid-cols-1 md:grid-cols-4 gap-12;
}

.footerBrand {
  @apply md:col-span-2;
}

.footerLogoWrapper {
  @apply flex items-center gap-3 mb-6;
}

.footerLogo {
  @apply h-10 w-auto;
  mix-blend-mode: screen;
  filter: drop-shadow(0 2px 8px rgba(222, 154, 73, 0.5)) brightness(1.1);
}

.footerBrandText {
  @apply text-leap-cream/60 max-w-md mb-8 text-sm leading-relaxed;
}

.footerSocialIcons {
  @apply flex gap-3;
}

.footerSocialIcon {
  @apply w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-leap-maroon/60 transition-colors cursor-pointer;
}

.footerColumnTitle {
  @apply font-bold text-xs uppercase tracking-widest text-leap-gold mb-6;
}

.footerColumnLinks {
  @apply space-y-4 text-leap-cream/60 text-sm;
}

.footerLink {
  @apply hover:text-white transition-colors;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
}

.footerLinkList {
  @apply space-y-4 text-leap-cream/60 text-sm;
}

.footerBottom {
  @apply container mx-auto mt-16 pt-8 border-t border-white/10 text-center text-leap-cream/40 text-xs tracking-wide;
}

/* ═══════════════════════════════════════════════════════════
   LOADING & UTILITY
═══════════════════════════════════════════════════════════ */
.loadingContainer {
  @apply min-h-screen flex items-center justify-center bg-leap-cream;
}

.loadingContent {
  @apply flex flex-col items-center gap-4;
}

.loadingText {
  @apply text-leap-olive text-sm font-medium tracking-wide uppercase;
}

/* ═══════════════════════════════════════════════════════════
   CARD MISC
═══════════════════════════════════════════════════════════ */
.cardOrgName {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #de9a49;
  margin-bottom: 0.35rem;
}

.cardMeta {
  @apply space-y-1.5 mb-4 text-sm text-leap-dark/60;
  font-size: 0.78rem;
}

.cardMetaItem {
  @apply flex items-center gap-2;
}

.cardMetaIcon {
  @apply text-leap-gold flex-shrink-0;
}

.cardHeaderLeft {
  @apply flex items-center gap-3;
}

.cardBadgeSection {
  @apply flex items-center gap-2;
}

.cardBadgeText {
  font-family: 'DM Sans', sans-serif;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #fefdf8;
  opacity: 0.9;
}

.adminCardDesc {
  @apply text-leap-olive mb-8 text-lg;
}

/* ═══════════════════════════════════════════════════════════
   MOBILE OVERRIDES
═══════════════════════════════════════════════════════════ */
@media (max-width: 768px) {
  /* Nav: collapse to 2-col [logo | hamburger] */
  .navInner {
    grid-template-columns: 1fr auto !important;
    padding: 0 1rem !important;
  }

  .navLogo {
    grid-column: 1 !important;
  }

  .navCenter {
    display: none !important;
  }

  .navRight {
    display: none !important;
  }

  .navMobileToggle {
    display: flex !important;
    gap: 0.5rem !important;
    align-items: center !important;
    grid-column: 2 !important;
    justify-self: end !important;
    align-self: center !important;
  }

  .navMobileBtn {
    color: #f9ecb6 !important;
  }

  .mobileMenu {
    display: block;
  }

  .heroLogo {
    width: min(320px, 82vw) !important;
  }

  .heroContent {
    padding: clamp(4.25rem, 10vh, 5.75rem) 0.75rem clamp(5.25rem, 13vh, 7rem);
  }

  .footerContainer {
    gap: 2rem !important;
  }

  .subthemesCompactInner {
    padding: 0 0.75rem;
    width: 100%;
  }

  .subthemesCompactShell {
    padding: 1.25rem 0;
  }
}

@media (max-width: 480px) {
  .navInner {
    padding: 0 0.75rem !important;
  }

  .heroLogo {
    width: min(280px, 78vw) !important;
  }

  .heroContent {
    padding: clamp(4rem, 9vh, 5.1rem) 0.65rem clamp(4.6rem, 11vh, 6rem);
  }

  .adminCard {
    padding: 1.5rem 1rem;
  }
}

.backToTopBtn {
  position: fixed;
  right: 1.1rem;
  bottom: 1.1rem;
  z-index: 980;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  border: 1px solid rgba(222, 154, 73, 0.55);
  background: linear-gradient(145deg, rgba(255, 252, 241, 0.96), rgba(253, 247, 228, 0.94));
  color: #334b46;
  border-radius: 999px;
  padding: 0.56rem 0.85rem;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(51, 75, 70, 0.2);
  backdrop-filter: blur(8px);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.backToTopBtn:hover {
  transform: translateY(-1px);
  border-color: rgba(222, 154, 73, 0.75);
  box-shadow: 0 16px 34px rgba(51, 75, 70, 0.24);
}

.mobileStickyCta {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  width: min(520px, calc(100vw - 1.5rem));
  bottom: calc(0.75rem + env(safe-area-inset-bottom));
  z-index: 970;
  display: none;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  border: 1px solid rgba(250, 225, 133, 0.45);
  border-radius: 999px;
  background: linear-gradient(135deg, #fae185 0%, #de9a49 55%, #c07830 100%);
  color: #1a1008;
  box-shadow: 0 14px 34px rgba(176, 90, 50, 0.35);
  padding: 0.8rem 1rem;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.2s ease, filter 0.2s ease;
}

.mobileStickyCta:hover {
  transform: translateX(-50%) translateY(-1px);
  filter: brightness(1.03);
}

@media (max-width: 768px) {
  .mobileStickyCta {
    display: inline-flex;
  }

  .backToTopBtn {
    bottom: calc(4.9rem + env(safe-area-inset-bottom));
    right: 0.85rem;
    min-width: 40px;
    min-height: 40px;
    padding: 0.5rem 0.7rem;
    font-size: 0.62rem;
    letter-spacing: 0.08em;
    box-shadow: 0 10px 26px rgba(51, 75, 70, 0.2);
  }
}

@media (max-width: 480px) {
  .mobileStickyCta {
    width: calc(100vw - 1rem);
    font-size: 0.64rem;
    letter-spacing: 0.08em;
    padding: 0.74rem 0.85rem;
  }

  .backToTopBtn {
    right: 0.65rem;
    bottom: calc(4.75rem + env(safe-area-inset-bottom));
  }
}